import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { filter } from 'rxjs/operators';
import { ActionMenuItem } from './models/action-menu-item.model';
import { ActionMenuService } from 'src/core/services/action-menu/action-menu.service';

@Component({
  selector: 'app-action-menu',
  standalone: true,
  imports: [CommonModule, MenuModule, TooltipModule],
  templateUrl: './action-menu.component.html',
  styleUrl: './action-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ActionMenuComponent implements OnChanges, AfterViewInit {
  private readonly menuService = inject(ActionMenuService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly instanceId = Symbol('action-menu');
  private previousSignature = '';

  @Input() items: ActionMenuItem[] = [];
  @ViewChild('menu') private readonly menuRef!: Menu;

  menuItems: MenuItem[] = [];

  get hasVisibleItems(): boolean {
    return this.menuItems.length > 0;
  }

  ngOnChanges(): void {
    const visibleItems = this.items.filter(item => item.visible !== false);
    const signature = visibleItems
      .map(
        ({ label, icon, severity, disabled }) =>
          `${label}|${icon ?? ''}|${severity ?? ''}|${disabled ?? false}`,
      )
      .join('||');

    if (signature === this.previousSignature) {
      return;
    }

    this.previousSignature = signature;

    this.menuItems = visibleItems.map(({ label, icon, severity, disabled, action, tooltip }) => ({
      label,
      icon,
      disabled,
      styleClass: severity ? `action-menu__item--${severity}` : '',
      data: { tooltip },
      command: () => {
        if (disabled) {
          return;
        }

        action();
      },
    }));
  }

  ngAfterViewInit(): void {
    this.menuService.opened$
      .pipe(
        filter(sourceId => sourceId !== this.instanceId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.menuRef.hide());
  }

  onToggle(event: Event): void {
    this.menuService.notifyOpen(this.instanceId);
    this.menuRef.toggle(event);
  }
}
